/**
 * Screen 4 — Members.
 *
 * Search first: the box is autofocused, so the common action (find someone who
 * is standing at the desk) is just typing. Name, phone and SK ID all match, in
 * whatever form a human types them.
 *
 * The status chips and the class filter combine, so "everyone in Kids Judo
 * whose subscription has expired" is two taps.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  filterMemberRows,
  filterMemberRowsByClass,
  filterMemberRowsByStatus,
  loadMemberClassIndex,
  loadMemberRows,
  type MemberRow,
} from '../db/queries';
import { StatusBadge, BalanceDueBadge } from '../components/StatusBadge';
import ClassFilter from '../components/ClassFilter';
import { classLabel } from '@shared/catalog';
import { formatDisplayDate, todayISO } from '@shared/dates';
import { formatPhone } from '@shared/identity';
import { describeRemaining } from '@shared/pricing';
import type { SubscriptionStatus } from '@shared/types';

type Filter = SubscriptionStatus | 'all' | 'due';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expiring_soon', label: 'Expiring' },
  { key: 'expired', label: 'Expired' },
  { key: 'frozen', label: 'Frozen' },
  { key: 'due', label: 'Balance due' },
];

export default function Members() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const filter = (params.get('status') as Filter) || 'all';
  const classKey = params.get('class') || 'all';

  const rows = useLiveQuery(() => loadMemberRows(todayISO()), [], undefined);
  const classIndex = useLiveQuery(() => loadMemberClassIndex(), [], new Map<string, string[]>());

  const visible = useMemo(() => {
    if (!rows) return [];
    return filterMemberRows(
      filterMemberRowsByClass(filterMemberRowsByStatus(rows, filter), classKey, classIndex),
      query
    );
  }, [rows, filter, classKey, classIndex, query]);

  /** Counts respect the class filter, so the chips describe what you can see. */
  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: 0,
      active: 0,
      expiring_soon: 0,
      expired: 0,
      cancelled: 0,
      frozen: 0,
      due: 0,
    };
    if (!rows) return c;
    const scoped = filterMemberRowsByClass(rows, classKey, classIndex);
    c.all = scoped.length;
    for (const r of scoped) {
      if (r.current) c[r.current.status]++;
      if (r.totalOutstanding > 0) c.due++;
    }
    return c;
  }, [rows, classKey, classIndex]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-4xl text-brand">Members</h1>
        <Link to="/new" className="btn">
          + New subscription
        </Link>
      </div>

      <input
        autoFocus
        className="field mb-4 text-xl"
        placeholder="Search name, phone or SK ID…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        dir="auto"
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setParam('status', f.key)}
            className={`rounded-btn border-2 px-4 py-2 text-sm font-bold uppercase tracking-button
                        transition-all duration-brand ${
                          filter === f.key
                            ? 'border-brand bg-brand text-paper'
                            : 'border-edge bg-surface text-white/60 hover:border-brand'
                        }`}
          >
            {f.label}
            <span className="tabular ml-2 opacity-60">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      <ClassFilter value={classKey} onChange={(k) => setParam('class', k)} className="mb-5" />

      {rows === undefined ? (
        <p className="py-16 text-center text-white/30">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="mb-2 text-lg text-white/50">
            {counts.all === 0 && classKey === 'all' ? 'No members yet' : 'Nobody matches that'}
          </p>
          {counts.all === 0 && classKey === 'all' && (
            <Link to="/new" className="btn mt-4">
              Add the first member
            </Link>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map((row) => (
            <MemberListRow key={row.member.id} row={row} />
          ))}
        </ul>
      )}

      {visible.length > 0 && (
        <p className="mt-4 text-center text-sm text-white/30">
          {visible.length} {visible.length === 1 ? 'member' : 'members'}
          {classKey !== 'all' && ` in ${classLabel(classKey)}`}
        </p>
      )}
    </div>
  );
}

function MemberListRow({ row }: { row: MemberRow }) {
  const { member, current, totalOutstanding } = row;

  return (
    <li>
      <Link
        to={`/members/${member.id}`}
        className="card flex flex-wrap items-center gap-x-5 gap-y-2 p-4
                   transition-all duration-brand hover:border-brand"
      >
        <div className="tabular w-20 shrink-0 font-bold text-brand">{member.sk_id}</div>

        <div className="min-w-[180px] flex-1">
          <div className="text-lg font-semibold" dir="auto">
            {member.full_name}
          </div>
          <div className="tabular text-sm text-white/40">{formatPhone(member.phone)}</div>
        </div>

        <div className="min-w-[150px] flex-1">
          {current ? (
            <>
              <div className="text-sm">{classLabel(current.subscription.class_key)}</div>
              <div className="text-sm text-white/40">
                {formatDisplayDate(current.subscription.end_date)} ·{' '}
                {current.frozenUntil
                  ? `frozen to ${formatDisplayDate(current.frozenUntil)}`
                  : describeRemaining(current.daysRemaining)}
              </div>
            </>
          ) : (
            <span className="text-sm text-white/30">No active subscription</span>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {current && <StatusBadge status={current.status} size="sm" />}
          <BalanceDueBadge remaining={totalOutstanding} size="sm" />
        </div>
      </Link>
    </li>
  );
}
